import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { boxScanQueryKey } from '@/hooks/useBoxScan'
import { taskScanApi } from '@/lib/api/taskScan'
import type { BoxScanView } from '@/types/task-scan'

interface ScanWSMessage {
  type: string
  item: BoxScanView
}

/** Live box updates via WebSocket (replaces fast polling when connected). */
export function useBoxScanWebSocket(
  taskUuid: string | undefined,
  boxNum: number | undefined,
  enabled: boolean,
) {
  const qc = useQueryClient()
  const [connected, setConnected] = useState(false)

  useEffect(() => {
    if (!enabled || !taskUuid || !boxNum) {
      setConnected(false)
      return
    }

    let ws: WebSocket | null = null
    let retryTimer: ReturnType<typeof setTimeout> | undefined
    let closed = false

    const connect = () => {
      ws = new WebSocket(taskScanApi.boxWebSocketUrl(taskUuid, boxNum))
      ws.onopen = () => setConnected(true)
      ws.onclose = () => {
        setConnected(false)
        if (!closed) {
          retryTimer = setTimeout(connect, 2000)
        }
      }
      ws.onerror = () => ws?.close()
      ws.onmessage = ev => {
        try {
          const msg = JSON.parse(String(ev.data)) as ScanWSMessage
          if (msg.type === 'box' && msg.item) {
            qc.setQueryData(boxScanQueryKey(taskUuid, boxNum), msg.item)
          }
        } catch {
          // ignore malformed frames
        }
      }
    }

    connect()
    return () => {
      closed = true
      if (retryTimer) clearTimeout(retryTimer)
      ws?.close()
      setConnected(false)
    }
  }, [taskUuid, boxNum, enabled, qc])

  return connected
}
